import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import type { PatientDetail } from '@/features/patients/types';
import { usePatientPhoto } from '@/features/patients/use-patient-photo';
import styles from './PatientPhoto.module.css';

const PHOTO_LABEL = 'Set by the patient, not an ID check';

/**
 * RL-1157: the profile photo the patient set in the app (RL-951 `photo`),
 * always labelled "Set by the patient, not an ID check": compare it with the
 * MyKad or passport. Also shown while the app account's identity is pending
 * (with that said). Fetched with the session (audited, never cached); no
 * photo, or no patients:read, shows the placeholder text.
 */
export function PatientPhoto({
  patient,
}: {
  patient: Pick<PatientDetail, 'id' | 'fullName' | 'photo' | 'appAccount'>;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const photo = patient.photo ?? null;
  const allowed = hasPermission('patients:read');
  const state = usePatientPhoto(
    patient.id,
    photo && allowed ? `${photo.id}|${photo.updatedAt}` : null,
  );
  const pending = patient.appAccount?.identityPending ?? false;

  if (!photo)
    return <span className={styles.meta}>No photo set in the app</span>;
  if (!allowed)
    return (
      <span className={styles.meta}>
        Photo set in the app (not shown for your role)
      </span>
    );
  return (
    <figure className={styles.photo} aria-busy={state.status === 'loading'}>
      {state.status === 'loading' && (
        <span className={styles.meta}>
          <Spinner size={14} /> Loading photo…
        </span>
      )}
      {state.status === 'ready' && (
        <img
          className={styles.image}
          src={state.url}
          alt={`Photo of ${patient.fullName} set by the patient`}
        />
      )}
      {state.status === 'none' && (
        <span className={styles.meta}>No photo set in the app</span>
      )}
      {state.status === 'error' && (
        <>
          <span className={styles.error} role="alert">
            Could not load the photo. {state.message}
            {state.requestId && (
              <> {t('common.requestId', { id: state.requestId })}</>
            )}
          </span>
          <Button
            size="sm"
            variant="ghost"
            icon={<RotateCcw size={14} />}
            onClick={state.retry}
          >
            Try again
          </Button>
        </>
      )}
      {state.status !== 'none' && (
        <figcaption className={styles.label}>{PHOTO_LABEL}</figcaption>
      )}
      {state.status !== 'none' && pending && (
        <span className={styles.pending}>
          Identity pending: the app account is not confirmed at the counter yet.
          Check the MyKad or passport.
        </span>
      )}
    </figure>
  );
}
